import React from 'react';
import { Divider, SparkIcon, Text, Skeleton } from '@spark';
import { formatSize, getLanguageName } from '@/shared/utils/helpers';
import {
    languageSharePercent,
    summaryStatsRecordCount,
} from '@/features/datasets/utils/datasetSummaryStats';
import '@/features/datasets/components/ExploreDatasets/Statistics.css';

function StatCard({ icon, title, scrollable = false, children }) {
    return (
        <div className={`spark-statistics-card${scrollable ? ' spark-statistics-card--scrollable' : ''}`}>
            <div className="spark-statistics-card-header">
                <SparkIcon slug={icon} size="small" />
                <Text variant="base-semibold" as="span" color="primary">{title}</Text>
            </div>
            <Divider />
            <div className="spark-statistics-card-body">
                {scrollable
                    ? <div className="spark-statistics-card-scroll">{children}</div>
                    : children}
            </div>
        </div>
    );
}

function MetricRow({ label, value }) {
    return (
        <div className="spark-statistics-metric-row">
            <Text variant="base-regular" as="span" color="secondary">{label}</Text>
            <Text variant="base-semibold" as="span" color="primary">{value}</Text>
        </div>
    );
}

function LoadingSkeleton({ rows = 3 }) {
    return (
        <div className="spark-statistics-skeleton">
            {Array.from({ length: rows }, (_, i) => (
                <div key={i} className="spark-statistics-metric-row">
                    <Skeleton shapeType="paragraph" width="40%" />
                    <Skeleton shapeType="paragraph" width="30%" />
                </div>
            ))}
        </div>
    );
}

function CountCard({ datasetMetaData, loading }) {
    if (loading) return <LoadingSkeleton rows={4} />;

    const entries = datasetMetaData?.num_records;
    if (!entries) return null;

    return (
        <>
            <MetricRow label="Dataset Size" value={formatSize(datasetMetaData.num_characters.sum)} />
            <MetricRow label="Dataset Entries" value={entries} />
            <MetricRow label="Num of Words" value={datasetMetaData.num_words.sum.toLocaleString()} />
            <MetricRow label="Num of Characters" value={datasetMetaData.num_characters.sum.toLocaleString()} />
        </>
    );
}

function LanguageCard({ datasetMetaData, loading }) {
    if (loading) return <LoadingSkeleton rows={2} />;

    const { language } = datasetMetaData;
    if (!language) return null;

    return (
        <>
            {Object.entries(language).map(([code, count]) => {
                const name = getLanguageName(code);
                const percent = languageSharePercent(count, language).toFixed(1);
                return (
                    <MetricRow
                        key={code}
                        label={name}
                        value={`${percent}% (${count})`}
                    />
                );
            })}
        </>
    );
}

function LengthCard({ datasetMetaData, loading }) {
    if (loading) return <LoadingSkeleton rows={3} />;

    const { num_characters, num_words } = datasetMetaData;
    if (!num_characters || !num_words) return null;

    const numRecords = summaryStatsRecordCount(datasetMetaData);
    const wordsAvg = numRecords ? (num_words.sum / numRecords).toFixed(1) : '0';
    const charsAvg = numRecords ? (num_characters.sum / numRecords).toFixed(1) : '0';

    return (
        <div className="spark-statistics-length-grid">
            <div className="spark-statistics-length-header">
                <Text variant="detail-medium" as="span" color="secondary" />
                <Text variant="detail-medium" as="span" color="secondary">Words</Text>
                <Text variant="detail-medium" as="span" color="secondary">Characters</Text>
            </div>
            <Divider />
            <div className="spark-statistics-length-row">
                <Text variant="base-regular" as="span" color="secondary">Average</Text>
                <Text variant="base-semibold" as="span" color="primary">{wordsAvg}</Text>
                <Text variant="base-semibold" as="span" color="primary">{charsAvg}</Text>
            </div>
            <div className="spark-statistics-length-row">
                <Text variant="base-regular" as="span" color="secondary">Maximum</Text>
                <Text variant="base-semibold" as="span" color="primary">{num_words.max.toLocaleString()}</Text>
                <Text variant="base-semibold" as="span" color="primary">{num_characters.max.toLocaleString()}</Text>
            </div>
            <div className="spark-statistics-length-row">
                <Text variant="base-regular" as="span" color="secondary">Minimum</Text>
                <Text variant="base-semibold" as="span" color="primary">{num_words.min.toLocaleString()}</Text>
                <Text variant="base-semibold" as="span" color="primary">{num_characters.min.toLocaleString()}</Text>
            </div>
        </div>
    );
}

export default function Statistics({ datasetMetaData = {} }) {
    const loading = Object.keys(datasetMetaData).length === 0;

    return (
        <div className="spark-statistics-root fs-unmask">
            <div className="spark-statistics-row">
                <StatCard icon="report-analytics-icon" title="Count">
                    <CountCard datasetMetaData={datasetMetaData} loading={loading} />
                </StatCard>

                <StatCard icon="text" title="Length">
                    <LengthCard datasetMetaData={datasetMetaData} loading={loading} />
                </StatCard>

                <StatCard icon="globe" title="Language" scrollable>
                    <LanguageCard datasetMetaData={datasetMetaData} loading={loading} />
                </StatCard>
            </div>
        </div>
    );
}
